import { Component, Inject } from '@angular/core';
import {
    AbstractControl,
    FormBuilder,
    FormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { CMPS_DIALOG_DATA, CmpsDialogRef } from '@cmps/ng-uikit';

import { DiffItem } from '../../coaching-base/diff-decorator';
import {
    ApprovalItem,
    ApprovalNote,
    EntityType,
} from '../../coaching-base/general';
import { NoteDetailFormValue } from '../../coaching-note/note-detail-form-value.model';
import { createNoteDetail } from '../../coaching-note/note-detail.form';
import { TranslationKeyMap } from '../coaching-commitment.i18n';

import {
    CommitmentSummaryDialogData,
    CommitmentSummaryDialogResult,
    CommitmentSummaryDialogResultAction,
    CommitmentSummaryViewMode,
    DiffItemFormValue,
} from './commitment-summary-dialog.model';

function noteContentsRequired(fg: FormGroup): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        // eslint-disable-next-line @typescript-eslint/dot-notation
        const isDisputed = fg.controls['approved'].value === false;
        const isEmpty = !control.value || !control.value.contents;
        if (isDisputed && isEmpty) {
            return { noteContents: null };
        }
        return null;
    };
}
@Component({
    selector: 'cmps-commitment-summary-dialog',
    templateUrl: './commitment-summary-dialog.component.html',
    host: {
        class: 'cmps-commitment-summary-dialog',
    },
})
export class CommitmentSummaryDialog {
    /** @private */ _i18n = TranslationKeyMap;
    /** @private */ _entityType = EntityType;
    /** @private */ _CommitmentDetailDialogResultAction =
        CommitmentSummaryDialogResultAction;

    /** @private */ _commitmentSummaryViewMode = CommitmentSummaryViewMode;

    private readonly _approvalItems: ApprovalItem[];
    /** @private */ _approvalItemButtons: {
        [diffItemContextId: string]: boolean;
    };

    /** @private */ _isSendDisabled: boolean;

    /** @private */ _form: FormGroup;

    constructor(
        private readonly _formBuilder: FormBuilder,

        /** @private */ public readonly _dialogRef: CmpsDialogRef<
            CommitmentSummaryDialog,
            CommitmentSummaryDialogResult
        >,
        /** @private */ @Inject(CMPS_DIALOG_DATA)
        public readonly _options: CommitmentSummaryDialogData,
    ) {
        const items = {};
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        _options.data &&
            _options.data.forEach((e) => {
                const fg = this._formBuilder.group({
                    approved: [null, [Validators.required]],
                    comment: [createNoteDetail()],
                });
                // eslint-disable-next-line @typescript-eslint/dot-notation
                fg.controls['comment'].setValidators([
                    Validators.required,
                    noteContentsRequired(fg),
                ]);
                items[e.diffItemContextId] = fg;
            });
        this._form = this._formBuilder.group(items);
        this._approvalItems = [];
        this._approvalItemButtons = {};

        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        _options.data &&
            _options.data.forEach((e) =>
                this._setDiffItemApproval(
                    e.diffItemId,
                    e.diffItemContextId,
                    undefined,
                ),
            );
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        _options.state &&
            _options.state.forEach((e) =>
                this._setDiffItemApproval(
                    e.diffItemId,
                    e.diffItemContextId,
                    e.approved,
                    e.notes,
                ),
            );
        this._checkCanSend();
    }

    /** @private */
    _onSignOffClick(): void {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._options.signOff && this._options.signOff();
    }

    /** @private */
    _onDisputeClick(): void {
        this._options.summaryView = CommitmentSummaryViewMode.DISPUTE_VIEW;
        this._disputeButtonAction();
        this._checkCanSend();
    }

    /** @private */
    _onAcceptAllClick(): void {
        this._acceptAll();
    }

    /** @private */
    _onAcceptItemClick(diffItem: DiffItem): void {
        this._setDiffItemApproval(
            diffItem.diffItemId,
            diffItem.diffItemContextId,
            true,
        );
    }

    /** @private */
    _onDisputeItemClick(diffItem: DiffItem): void {
        this._setDiffItemApproval(
            diffItem.diffItemId,
            diffItem.diffItemContextId,
            false,
        );
    }

    /** @private */
    _onSendAcceptAllClick(): void {
        this._acceptAll();
        this._applyNotes();
        this._dialogRef.close({
            action: CommitmentSummaryDialogResultAction.SEND_SIGN_OFF,
            items: this._approvalItems,
        });
    }

    /** @private */
    _onSendClick(): void {
        this._validateFormManually();
        if (!this._form.valid) {
            return;
        }
        this._applyNotes();
        this._dialogRef.close({
            action: CommitmentSummaryDialogResultAction.SEND_SIGN_OFF,
            items: this._approvalItems,
        });
    }

    private _acceptAll(): void {
        this._options.data.forEach((e) =>
            this._setDiffItemApproval(e.diffItemId, e.diffItemContextId, true),
        );
    }

    private _applyNotes(): void {
        Object.keys(this._form.controls).forEach((key) => {
            const formControl = this._form.get(key);
            const item = this._approvalItems.find(
                (e) => e.diffItemContextId === key,
            );
            if (item && formControl.value.approved === false) {
                item.notes = [];
                item.notes.push({
                    noteId: undefined,
                    contents: formControl.value.comment.contents,
                });
            } else {
                item.notes = [];
            }
            item.approved = formControl.value.approved;
        });
    }

    private _setDiffItemApproval(
        diffItemId: string,
        diffItemContextId: string,
        approved: boolean,
        notes?: ApprovalNote[],
    ): void {
        let approvalItem = this._approvalItems.find(
            (e) =>
                e.diffItemId === diffItemId &&
                e.diffItemContextId === diffItemContextId,
        );
        if (!approvalItem) {
            approvalItem = { diffItemId, diffItemContextId };
            this._approvalItems.push(approvalItem);
        }
        const item = this._form.get(diffItemContextId) as FormGroup;
        const nextValue: DiffItemFormValue = item
            ? { ...item.value }
            : { approved, comment: {} };
        nextValue.approved = approved;
        this._approvalItemButtons[diffItemContextId] = approved;
        const value = createNoteDetail();
        nextValue.comment = value;
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (notes && notes.length) {
            value.contents = notes[0].contents;
        }
        if (item) {
            item.patchValue(nextValue);
        } else {
            this._form.patchValue({
                [diffItemContextId]: nextValue,
            });
        }
        this._checkCanSend();
    }

    private _disputeButtonAction(): void {
        Object.keys(this._form.controls).forEach((key) => {
            const a = this._form.get(key) as FormGroup;
            const nextValue: {
                approved: boolean;
                comment: NoteDetailFormValue;
            } = { ...a.value };
            nextValue.approved = null;
            a.patchValue(nextValue);
        });
    }

    private _validateFormManually(): void {
        Object.keys(this._form.controls).forEach((key) => {
            const commentControl = this._form.get(`${key}.comment`);
            commentControl.markAsDirty();
        });
    }

    private _checkCanSend(): void {
        this._isSendDisabled = Object.values(this._approvalItemButtons).some(
            (e) => e == null,
        );
    }
}
